import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { locationAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiMapPin, FiPlus, FiEdit2, FiTrash2, FiStar, FiHome, FiBriefcase } from 'react-icons/fi';
import './Locations.css';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getAll();
      setLocations(response.data.locations || []);
    } catch (error) {
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await locationAPI.setDefault(id);
      toast.success('Default location updated');
      fetchLocations();
    } catch (error) {
      toast.error('Failed to set default');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      await locationAPI.delete(id);
      toast.success('Location deleted');
      fetchLocations();
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  const getTypeIcon = (type) => {
    const icons = { home: <FiHome />, work: <FiBriefcase /> };
    return icons[type] || <FiMapPin />;
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="locations-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1>My Locations</h1>
            <p>Save your delivery addresses with detailed instructions</p>
          </div>
          <Link to="/locations/new" className="btn btn-primary">
            <FiPlus /> Add Location
          </Link>
        </div>

        {locations.length === 0 ? (
          <div className="empty-state card">
            <FiMapPin className="empty-icon" />
            <h3>No locations saved yet</h3>
            <p>Add your first location to make deliveries easier</p>
            <Link to="/locations/new" className="btn btn-primary">
              <FiPlus /> Add Your First Location
            </Link>
          </div>
        ) : (
          <div className="locations-grid">
            {locations.map(location => (
              <div key={location._id} className={`location-card card ${location.isDefault ? 'default' : ''}`}>
                {location.isDefault && (
                  <div className="default-badge">
                    <FiStar /> Default
                  </div>
                )}
                <div className="location-header">
                  <div className="location-type">
                    {getTypeIcon(location.type)}
                  </div>
                  <h3>{location.label}</h3>
                </div>

                <div className="location-address">
                  <p>{location.address?.full}</p>
                  {location.address?.flatNumber && (
                    <p className="flat-info">
                      {location.address.building && `${location.address.building}, `}
                      {location.address.flatNumber}
                      {location.address.floor && `, ${location.address.floor}`}
                    </p>
                  )}
                </div>

                {location.deliveryInstructions?.directions && (
                  <div className="location-instructions">
                    <strong>Directions:</strong>
                    <p>{location.deliveryInstructions.directions}</p>
                  </div>
                )}

                {location.gateSystem?.type && location.gateSystem.type !== 'none' && (
                  <div className="gate-info">
                    <span className="gate-badge">{location.gateSystem.type}</span>
                  </div>
                )}

                {location.photos && location.photos.length > 0 && (
                  <div className="location-photos">
                    {location.photos.slice(0, 3).map((photo, i) => (
                      <img key={i} src={photo.url} alt={photo.caption || 'Location'} />
                    ))}
                    {location.photos.length > 3 && (
                      <span className="more-photos">+{location.photos.length - 3}</span>
                    )}
                  </div>
                )}

                <div className="location-actions">
                  {!location.isDefault && (
                    <button
                      className="action-btn"
                      onClick={() => handleSetDefault(location._id)}
                      title="Set as default"
                    >
                      <FiStar />
                    </button>
                  )}
                  <Link to={`/locations/${location._id}/edit`} className="action-btn" title="Edit">
                    <FiEdit2 />
                  </Link>
                  <button
                    className="action-btn danger"
                    onClick={() => handleDelete(location._id)}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Locations;
